import { Button, Skeleton } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { QuickWinIllustration } from "@/icons"
import { QUICK_WINS_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { useQuickWinsData } from "@/pages/TriagePage/application/useQuickWins"

import styles from "./QuickWinIssueBanners.module.scss"

import type { QuickWin } from "@/domain/issue"
import type { QuickWinIssueBannersProps } from "./QuickWinIssueBanners.types"

export const QuickWinIssueBanners = ({
  issueId,
  projectId
}: QuickWinIssueBannersProps) => {
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()
  const { abbreviateNumber } = useNumberConvertor()
  const { open } = useDrawer(QUICK_WINS_DRAWER_ID)

  const { response, isLoading } = useQuickWinsData(projectId, {
    issueId
  })

  const quickWins = response?.data || []

  if (!isLoading && quickWins.length === 0) return null

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.banner}>
          <div className={styles.left}>
            <Skeleton.Avatar
              active
              shape="square"
              size={42}
              className={styles.skeletonAvatar}
            />
            <Skeleton.Input
              active
              size="small"
              className={styles.skeletonText}
            />
          </div>
          <Skeleton.Button
            active
            size="small"
            className={styles.skeletonButton}
          />
        </div>
      </div>
    )
  }

  const handleShowQuickWins = (quickWin: QuickWin) => {
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.ISSUE.BANNER_CLICK, {
      issueId,
      projectId,
      quickWinId: quickWin.id,
      quickWinType: quickWin.type,
      totalResolvedIssues: quickWin.totalIssues
    })
    open({
      record: {
        id: projectId,
        meta: {
          typeFilter: quickWin.type,
          searchQuery: quickWin.displayName,
          source: "issue_banner"
        }
      }
    })
  }

  return (
    <div className={styles.container}>
      {quickWins.map((quickWin) => {
        const additionalIssues = Math.max(quickWin.totalIssues - 1, 0)
        return (
          <div key={quickWin.id} className={styles.banner}>
            <div className={styles.left}>
              <QuickWinIllustration />
              <span className={styles.text}>
                <Trans
                  i18nKey="quickWins.issueBanner.text"
                  values={{ issueCount: abbreviateNumber(additionalIssues) }}
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
            <Button size="small" onClick={() => handleShowQuickWins(quickWin)}>
              {t("quickWins.issueBanner.showButton")}
            </Button>
          </div>
        )
      })}
    </div>
  )
}
